import React, { useRef, useState, useEffect, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Undo2, Eraser, PenLine, CheckCircle2 } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { onCompleteDigitalSignature } from "../../../redux/actions/requirementsActions";
import { Status } from "../../../redux/interfaces/folder.types";
import { useInvalidateVaultFolder } from "../../../hooks/vault/queries/useVaultFolder";
import { UploadedFiles } from "../UploadedFiles";
import { InvalidRequirementReason } from "../InvalidRequirementReason";
import { CancelToReview } from "../CancelToReview";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { toast } from "sonner";
import { cn } from "../../../lib/utils";
import { useSingleFlight } from "../../../hooks/useSingleFlight";

interface Props {
  folderId: string;
}

interface SignatureStroke {
  data: string;
}

export const DigitalSignature: React.FC<Props> = ({ folderId }) => {
  const signatureRef = useRef<SignatureCanvas>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [strokeHistory, setStrokeHistory] = useState<SignatureStroke[]>([]);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 114 });
  const { isPending: isSubmitting, run: runSingleFlight } = useSingleFlight();

  const { activeRequirement, activeVault, loading } = useAppSelector((state) => state.vault);
  const invalidateVaultFolder = useInvalidateVaultFolder(activeVault?.id);
  const requirementStatus = activeRequirement?.status as Status | undefined;

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;

  // Responsive canvas sizing
  useEffect(() => {
    if (!isOpen) return;

    const updateCanvasSize = () => {
      if (canvasContainerRef.current) {
        const containerWidth = canvasContainerRef.current.offsetWidth;
        const width = Math.min(Math.max(containerWidth - 2, 300), 560);
        const height = Math.max(Math.round(width * 0.5), 160);
        setCanvasSize({ width, height });
      }
    };

    // Small delay to ensure modal is rendered
    const timer = setTimeout(updateCanvasSize, 50);
    window.addEventListener("resize", updateCanvasSize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateCanvasSize);
    };
  }, [isOpen]);

  // Reset state when modal opens/closes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Reset to default state when opening
      setAcceptedTerms(true);
    } else {
      clearSignature();
    }
  };

  const checkSignatureContent = useCallback(() => {
    if (signatureRef.current) {
      setHasSignature(!signatureRef.current.isEmpty());
    }
  }, []);

  const handleStrokeEnd = useCallback(() => {
    if (signatureRef.current) {
      const data = signatureRef.current.toDataURL();
      setStrokeHistory((prev) => [...prev, { data }]);
      checkSignatureContent();
    }
  }, [checkSignatureContent]);

  const clearSignature = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setStrokeHistory([]);
      setHasSignature(false);
    }
  }, []);

  const handleUndo = useCallback(() => {
    if (strokeHistory.length <= 1) {
      clearSignature();
      return;
    }

    const newHistory = strokeHistory.slice(0, -1);
    setStrokeHistory(newHistory);

    if (signatureRef.current && newHistory.length > 0) {
      const lastState = newHistory[newHistory.length - 1];
      signatureRef.current.clear();
      signatureRef.current.fromDataURL(lastState.data, {
        width: canvasSize.width,
        height: canvasSize.height,
      });
      setHasSignature(true);
    } else if (signatureRef.current) {
      signatureRef.current.clear();
      setHasSignature(false);
    }
  }, [clearSignature, strokeHistory, canvasSize]);

  const dataURItoBlob = (dataURI: string): Blob => {
    const byteString = atob(dataURI.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: "image/png" });
  };

  const handleSubmit = async () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      toast.error("Por favor proporciona tu firma");
      return;
    }

    if (!acceptedTerms) {
      toast.error("Debes aceptar los términos para continuar");
      return;
    }

    if (!folderId) {
      toast.error("Error: No se pudo identificar el expediente");
      return;
    }

    await runSingleFlight(async () => {
      try {
        const trimmedCanvas = signatureRef.current!.getTrimmedCanvas();
        const blob = dataURItoBlob(trimmedCanvas.toDataURL("image/png"));

        const formData = new FormData();
        formData.append("signature", blob, "signature.png");
        formData.append("folderId", folderId);

        await dispatch(onCompleteDigitalSignature(formData)).unwrap();

        // Invalidate React Query cache so VaultPageRedesign gets fresh data
        invalidateVaultFolder();

        toast.success("Firma registrada correctamente");
        setIsOpen(false);
        clearSignature();
        setAcceptedTerms(false);
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Error al registrar la firma";
        toast.error(errorMessage);
      }
    });
  };

  const canSubmit = hasSignature && acceptedTerms && !isSubmitting;

  // Render valid/completed state (backend may return either valid or pending_review)
  if (activeRequirement?.status === Status.Valid || activeRequirement?.status === Status.PendingReview) {
    return (
      <div className="w-full flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center justify-center px-6 max-w-md w-full">
          <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mb-6 ring-8 ring-emerald-50/50">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>

          <h3 className="text-xl font-semibold text-slate-900 mb-2 text-center">
            Firma registrada
          </h3>
          <p className="text-sm text-slate-500 mb-8 text-center leading-relaxed">
            Tu firma autógrafa digital ha sido verificada y aprobada.
            Puedes continuar con el siguiente paso.
          </p>

          {/* Show uploaded files */}
          {activeRequirement?.files && activeRequirement.files.length > 0 && (
            <div className="w-full rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-3">
                Documento firmado
              </p>
              <UploadedFiles />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <p className="text-gray-600">
          Proporciona tu{" "}
          <span className="font-semibold text-gray-900">
            firma autógrafa digital
          </span>{" "}
          para validar la autenticidad de tu información.
        </p>
      </div>

      {/* Invalid Requirement Reason — shown at the top so the rejection
          message is visible before the user scrolls through the form. */}
      {activeRequirement && (
        <InvalidRequirementReason requirement={activeRequirement} />
      )}

      {/* Content */}
      <div className="space-y-6">
        {/* Signature Card - Opens Modal */}
        {enabledInputs && (
          <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <button
                type="button"
                className="w-full group cursor-pointer"
              >
                <div className="relative rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100 transition-all p-8">
                  {/* Signature preview area */}
                  <div className="flex flex-col items-center justify-center min-h-[120px]">
                    <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform">
                      <PenLine className="h-5 w-5 text-slate-400 group-hover:text-slate-600" />
                    </div>
                    <p className="text-sm font-medium text-slate-700 mb-1">
                      Haz clic para firmar
                    </p>
                    <p className="text-xs text-slate-400">
                      Se abrirá un panel para capturar tu firma
                    </p>
                  </div>

                  {/* Decorative signature line */}
                  <div className="absolute bottom-6 left-12 right-12">
                    <div className="border-b border-dashed border-slate-300" />
                  </div>
                </div>
              </button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-lg p-0 gap-0 overflow-hidden">
              <DialogHeader className="px-6 pt-6 pb-2">
                <DialogTitle>Firma el documento</DialogTitle>
              </DialogHeader>

              {/* Instructions */}
              <div className="px-6">
                <ul className="text-sm text-slate-500 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                    Usa tu dedo o mouse para firmar
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full bg-slate-400" />
                    Debe ser legible y similar a tu firma oficial
                  </li>
                </ul>
              </div>

              {/* Signature Canvas */}
              <div className="px-6 py-4">
                <div
                  ref={canvasContainerRef}
                  className="relative rounded-lg bg-slate-100 overflow-hidden"
                >
                  <SignatureCanvas
                    ref={signatureRef}
                    penColor="#1a1a1a"
                    minWidth={1.5}
                    maxWidth={3.5}
                    velocityFilterWeight={0.7}
                    onEnd={handleStrokeEnd}
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: "w-full touch-none",
                      style: {
                        width: "100%",
                        height: canvasSize.height,
                      },
                    }}
                  />

                  {/* Placeholder - disappears when signing */}
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-slate-400 text-sm">Firma aquí</span>
                    </div>
                  )}

                  {/* Action buttons inside canvas */}
                  <div className="absolute bottom-2 left-2 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={handleUndo}
                      disabled={strokeHistory.length === 0}
                      className={cn(
                        "flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors",
                        strokeHistory.length > 0
                          ? "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                          : "text-slate-400 cursor-not-allowed"
                      )}
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                      Deshacer
                    </button>

                    <span className="text-slate-300">|</span>

                    <button
                      type="button"
                      onClick={clearSignature}
                      disabled={!hasSignature}
                      className={cn(
                        "flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors",
                        hasSignature
                          ? "text-slate-600 hover:text-slate-900 hover:bg-white/60"
                          : "text-slate-400 cursor-not-allowed"
                      )}
                    >
                      <Eraser className="h-3.5 w-3.5" />
                      Limpiar
                    </button>
                  </div>
                </div>
              </div>

              {/* Terms Toggle */}
              <div className="px-6 pt-4">
                <div
                  className={cn(
                    "flex items-start gap-3 p-4 rounded-lg border-2 transition-all cursor-pointer",
                    acceptedTerms
                      ? "bg-slate-900 border-slate-900"
                      : "bg-white border-slate-200 hover:border-slate-300"
                  )}
                  onClick={() => setAcceptedTerms(!acceptedTerms)}
                >
                  <Switch
                    checked={acceptedTerms}
                    onCheckedChange={setAcceptedTerms}
                    className={cn(
                      "mt-0.5 flex-shrink-0 pointer-events-none",
                      acceptedTerms && "data-[state=checked]:bg-white [&>span]:bg-slate-900"
                    )}
                  />
                  <p
                    className={cn(
                      "text-sm leading-relaxed transition-colors select-none",
                      acceptedTerms ? "text-white" : "text-slate-600"
                    )}
                  >
                    Al firmar, confirmo que he leído y acepto los{" "}
                    <span
                      className={cn(
                        "font-medium",
                        acceptedTerms ? "text-white" : "text-slate-900"
                      )}
                    >
                      términos y condiciones
                    </span>
                    , y declaro bajo protesta de decir verdad que la información
                    proporcionada es auténtica.
                  </p>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex gap-3 px-6 py-4 mt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setIsOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleSubmit}
                  disabled={!canSubmit || loading}
                >
                  {isSubmitting ? "Enviando..." : "Firmar documento"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Uploaded Files */}
        <UploadedFiles />

        {/* Cancel to Review (when pending review) */}
        {activeRequirement && requirementStatus === Status.PendingReview && (
          <CancelToReview requirement={activeRequirement} />
        )}
      </div>
    </div>
  );
};
